import {useMemo, useCallback} from 'react'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import type {
	ExtendedBlock,
	Tersed,
	PageConfigContentItem,
} from '@/_new-code/services/kontent-ai/types'
import type {ImageContentItem} from '@/_new-code/products/flexible-web-toolkit/blocks/image'
import {getBrandColor} from '@/_new-code/products/flexible-web-toolkit/blocks/helpers'
import {logError, logMessage} from '@/_new-code/services/logger/client'
import {
	MediaSlider,
	type MediaItem,
	type MediaSliderConfig,
} from '@/_new-code/products/flexible-web-toolkit/components/slider/media-slider'
import type {WistiaContentItem} from '../videos/wistia'
import type {VideoBlockContentItem} from '../videos/video-block'
import {WistiaBlock} from '../videos/wistia'
import {VideoBlockBlock} from '../videos/video-block'
import {getVideoDetails} from '../videos/video-types'

export type PDPSliderContentItem = IContentItem<{
	enlargeTextImage: Elements.TextElement
	enlargeTextVideo: Elements.TextElement
	videoImage: Elements.LinkedItemsElement<
		WistiaContentItem | VideoBlockContentItem | ImageContentItem
	>
	productTitle: Elements.TextElement
}>

type SliderConfig = MediaSliderConfig

export const SliderBlock: ExtendedBlock<
	PDPSliderContentItem,
	{
		config?: SliderConfig
		pageConfig?: Tersed<PageConfigContentItem> // Allow parent to override page config
		onMediaChange?: (mediaItem: MediaItem, index: number) => void
		className?: string
		containerClassName?: string
		mainImageClassName?: string
		thumbnailClassName?: string
		fullWidth?: boolean
	}
> = ({
	block,
	config = {},
	pageConfig: overridePageConfig,
	onMediaChange,
	className,
	containerClassName = '',
	mainImageClassName = '',
	thumbnailClassName = ' p-3',
	fullWidth = true,
	...context
}) => {
	// Use override pageConfig from parent if provided, otherwise fall back to context
	const pageConfig = overridePageConfig || context.page.elements.config[0]
	const brandColor = pageConfig ? getBrandColor(pageConfig) : null

	// Process media items from block elements with proper typing
	const mediaItems = useMemo((): MediaItem[] => {
		const videoImageElements = block.elements.videoImage

		if (!Array.isArray(videoImageElements)) {
			return []
		}

		return videoImageElements
			.map(
				(
					item: Tersed<
						| WistiaContentItem
						| VideoBlockContentItem
						| ImageContentItem
					>,
					index: number
				): MediaItem | null => {
					// Handle ImageContentItem
					if (item.system.type === 'image') {
						const imageItem = item as Tersed<ImageContentItem>
						const imageAsset = imageItem.elements.image[0]
						if (imageAsset) {
							return {
								id: item.system.id || `image-${index}`,
								url: String(imageAsset.url || ''),
								type: 'image' as const,
								alt: String(
									imageAsset.description ||
										`Image ${index + 1}`
								),
								title: item.system.name || undefined,
							}
						}
					}

					// Handle video content items (Wistia and YouTube)
					if (
						item.system.type === 'wistia' ||
						item.system.type === 'video_block'
					) {
						try {
							const videoDetails = getVideoDetails(
								item as Tersed<
									WistiaContentItem | VideoBlockContentItem
								>
							)

							// Get thumbnail URL for video_block type
							let thumbnailUrl = ''
							if (item.system.type === 'video_block') {
								const videoBlockItem =
									item as Tersed<VideoBlockContentItem>
								const posterFrame =
									videoBlockItem.elements.posterFrameImage[0]
								thumbnailUrl = posterFrame?.url || ''
							}

							return {
								id:
									item.system.id ||
									`${videoDetails.type}-${index}`,
								url: String(videoDetails.videoId),
								type: 'video' as const,
								videoType: videoDetails.type as
									| 'youtube'
									| 'wistia',
								alt: String(
									item.system.type === 'wistia'
										? (item as Tersed<WistiaContentItem>)
												.elements.alt ||
												`Wistia Video ${index + 1}`
										: (
												item as Tersed<VideoBlockContentItem>
											).elements.videoTitle ||
												`YouTube Video ${index + 1}`
								),
								title: item.system.name || undefined,
								thumbnail: thumbnailUrl,
							}
						} catch (error) {
							logError(error)
							logMessage(
								`PDP Slider context: Processing ${item.system.type} failed for item ${item.system.id}`
							)
							return null
						}
					}

					// Skip unknown types
					return null
				}
			)
			.filter((item): item is MediaItem => item !== null)
	}, [block.elements.videoImage])

	// Render video components for the slider
	const renderVideoComponent = useCallback(
		(item: MediaItem, forceInteractive = false) => {
			// Find the original content item to pass to the video blocks
			const videoImageElements = block.elements.videoImage
			const originalItem = videoImageElements.find(
				(element) => element.system.id === item.id
			)

			if (!originalItem || item.type !== 'video') {
				return <div>Invalid video content</div>
			}

			if (item.videoType === 'wistia') {
				return (
					<div className="h-full w-full">
						<WistiaBlock
							block={originalItem as Tersed<WistiaContentItem>}
							className="h-full w-full"
							{...context}
							{...(forceInteractive && {interactive: true})}
						/>
					</div>
				)
			}

			if (item.videoType === 'youtube') {
				return (
					<div className="h-full w-full">
						<VideoBlockBlock
							block={
								originalItem as Tersed<VideoBlockContentItem>
							}
							className="h-full w-full"
							{...context}
						/>
					</div>
				)
			}

			// Fallback for unrecognized video types
			return (
				<div className="bg-gray-50 relative flex h-full w-full items-center justify-center">
					<div className="text-gray-500">Unsupported video type</div>
				</div>
			)
		},
		[block.elements.videoImage, context]
	)

	// Merge CMS-specific config with provided config
	const sliderConfig: MediaSliderConfig = {
		arrowColor: brandColor || 'elanco-blue',
		...config,
		position: 'left',
	}

	return (
		<div data-kontent-element-codename={block.system.codename}>
			<MediaSlider
				className={className}
				config={sliderConfig}
				containerClassName={containerClassName}
				enlargeImageText={
					block.elements.enlargeTextImage || 'Enlarge Image'
				}
				enlargeVideoText={
					block.elements.enlargeTextVideo || 'Enlarge Video'
				}
				fullWidth={fullWidth}
				mainImageClassName={mainImageClassName}
				mediaItems={mediaItems}
				onMediaChange={onMediaChange}
				productTitleText={block.elements.productTitle}
				renderVideoComponent={renderVideoComponent}
				thumbnailClassName={thumbnailClassName}
			/>
		</div>
	)
}
